import React, {
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns'
import Device, { DeviceIDTypes } from '@data/device/model/device'
import { roundToTwo } from '@common/utils/helperFunctions'
import { COLUMNS } from '../../WaterUsage/constants'
import {
  DateTypes,
  deviceWaterUsage,
  WaterUsageQueryMultipleDevices,
} from '@data/waterUsage/model/waterUsage.model'
import { useAuthStore } from '@store/auth.store'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import {
  BsBarChartLine,
  BsTable,
} from 'react-icons/bs'
import { buildUsageNavigatorPdf } from './buildUsageNavigatorPdf'
import {
  useDevicesWaterUsage,
  useRefreshDeviceWaterUsage,
} from '@data/waterUsage/waterUsage'
import { hasPermissions } from '@common/utils/validation/authValidators'
import {
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { useMediaQuery } from '@mantine/hooks'

ChartJS.register(PointElement, Tooltip, Legend, Title, LinearScale, BarElement)

interface IUsageNavigatorProps {
  show: boolean
  deviceSelected: Device
  onHide: () => void
}

const formatHourLabel = (hour: number, useCompactLabels: boolean) => {
  if (!useCompactLabels) {
    return hour < 10 ? `0${hour}:00` : `${hour}:00`
  }

  const period = hour >= 12 ? 'pm' : 'am'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}${period}`
}

const buildData = (
  deviceInfo: Device,
  deviceUsage: deviceWaterUsage[],
  useCompactLabels: boolean,
  maxBarThickness: number,
  barPercentage: number,
  categoryPercentage: number,
) => {
  const labels = deviceUsage.map((usage: deviceWaterUsage) =>
    formatHourLabel(usage.hour, useCompactLabels),
  )
  const datasets = [
    {
      label: deviceInfo.deviceName,
      data: deviceUsage.map((usage: deviceWaterUsage) => usage.flow),
      fill: false,
      backgroundColor: 'rgb(255, 99, 132)',
      borderColor: 'rgba(255, 99, 132, 0.2)',
      maxBarThickness,
      barPercentage,
      categoryPercentage,
    },
  ]
  return { labels, datasets }
}

const getToday = () => startOfDay(new Date())

const UsageNavigator: React.FC<IUsageNavigatorProps> = ({
                                                                  show,
                                                                  deviceSelected,
                                                                  onHide,
                                                                }: IUsageNavigatorProps) => {
  const [currentDate, setCurrentDate] = useState<Date | null>(() => getToday())
  const [totalUsage, setTotalUsage] = useState<null | number>(null)
  const [activeTab, setActiveTab] = useState<string | null>('usage')

  const [enableFetchData, setEnableFetchData] = useState<boolean>(false)

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false)

  const permissions = useAuthStore((state) => state.permissions)
  const { data: userInfo } = useCurrentUserQuery()
  const { mutateAsync: refreshDeviceWaterUsage, isPending: isRefreshing } = useRefreshDeviceWaterUsage()
  const isSmallScreen = useMediaQuery('(max-width: 48em)')
  const isExtraLargeScreen = useMediaQuery('(min-width: 120em)')

  const chartRef = useRef<ChartJS<'bar'> | null>(null)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const resizeAnimationFrameRef = useRef<number | null>(null)

  const userUom = userInfo?.preferences?.uom
  const chartHeight = isFullscreen
    ? (isSmallScreen ? '320px' : isExtraLargeScreen ? '720px' : '70vh')
    : (isSmallScreen ? '320px' : 420)
  const axisTitleFontSize = isSmallScreen ? 11 : isExtraLargeScreen ? 14 : 12
  const tickFontSize = isSmallScreen ? 11 : isExtraLargeScreen ? 13 : 12
  const legendFontSize = isSmallScreen ? 11 : isExtraLargeScreen ? 14 : 12
  const chartPadding = isSmallScreen ? 4 : isExtraLargeScreen ? 20 : 12
  const maxBarThickness = isSmallScreen ? 18 : isExtraLargeScreen ? 34 : 26
  const barPercentage = isSmallScreen ? 0.7 : isExtraLargeScreen ? 0.8 : 0.75
  const categoryPercentage = isSmallScreen ? 0.78 : isExtraLargeScreen ? 0.86 : 0.82
  const chartContentMaxWidth = isExtraLargeScreen ? 1200 : 980

  const query: WaterUsageQueryMultipleDevices = {
    deviceIds: [deviceSelected.dUuid],
    startDate: currentDate ? format(currentDate, 'yyyy-MM-dd\'T\'HH:00:00') : '',
    endDate: currentDate ? format(currentDate, 'yyyy-MM-dd\'T\'23:59:59') : '',
    uom: userUom,
    idType: DeviceIDTypes.dUuid,
    dateType: DateTypes.timezone,
  }

  const {
    data: multipleDeviceUsage,
    isLoading,
    isFetching,
  } = useDevicesWaterUsage(query, enableFetchData)

  useEffect(() => {
    setEnableFetchData(show && !!deviceSelected.dUuid)
  }, [show, deviceSelected.dUuid])

  useEffect(() => {
    setCurrentDate(getToday())
    setActiveTab('usage')

    if (!show) {
      setEnableFetchData(false)
    }
  }, [show])

  useEffect(() => {
    if (multipleDeviceUsage) {
      setTotalUsage(multipleDeviceUsage.totalUsage)
    }
  }, [multipleDeviceUsage])

  useEffect(() => {
    if (!show || activeTab !== 'usage') {
      return
    }

    const scheduleChartResize = () => {
      if (resizeAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current)
      }

      resizeAnimationFrameRef.current = window.requestAnimationFrame(() => {
        chartRef.current?.resize()
        chartRef.current?.update('none')
      })
    }

    scheduleChartResize()

    if (typeof ResizeObserver === 'undefined' || !chartContainerRef.current) {
      return () => {
        if (resizeAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(resizeAnimationFrameRef.current)
          resizeAnimationFrameRef.current = null
        }
      }
    }

    const observer = new ResizeObserver(() => {
      scheduleChartResize()
    })

    observer.observe(chartContainerRef.current)

    return () => {
      observer.disconnect()
      if (resizeAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current)
        resizeAnimationFrameRef.current = null
      }
    }
  }, [activeTab, isFullscreen, show, multipleDeviceUsage])

  const onNextButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (currentDate) {
      setCurrentDate(addDays(currentDate, 1))
    }
  }

  const onRefreshButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (currentDate && isSameDay(currentDate, getToday())) {
      await refreshDeviceWaterUsage({ deviceId: deviceSelected.dUuid, params: query })
    }
  }

  const onPreviousButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (currentDate) {
      setCurrentDate(subDays(currentDate, 1))
    }
  }

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: chartPadding,
        right: chartPadding,
        bottom: chartPadding,
        left: chartPadding,
      },
    },
    plugins: {
      legend: {
        labels: {
          font: {
            size: legendFontSize,
          },
        },
      },
      tooltips: {
        mode: 'label',
        titleFontSize: 18,
        bodyFontSize: 18,
      },
    },
    scales: {
      x: {
        offset: true,
        title: {
          display: true,
          text: 'Hours',
          color: 'black',
          padding: 8,
          font: { size: axisTitleFontSize },
        },
        ...(isSmallScreen
          ? {
            ticks: {
              autoSkip: false,
              maxRotation: 55,
              minRotation: 55,
              font: {
                size: tickFontSize,
              },
            },
          }
          : isExtraLargeScreen
            ? {
              ticks: {
                autoSkip: false,
                maxRotation: 55,
                minRotation: 0,
                font: {
                  size: tickFontSize,
                },
              },
            }
            : {
              ticks: {
                font: {
                  size: tickFontSize,
                },
              },
            }),
      },
      y: {
        title: {
          display: true,
          text: `${userUom}`,
          color: 'black',
          font: { size: axisTitleFontSize },
        },
        ticks: {
          beginAtZero: true,
          suggestedMin: 0,
          font: {
            size: tickFontSize,
          },
        },
      },
    },
  }

  const downloadWaterGraph = () => {
    const chartImageData = chartRef.current?.toBase64Image?.()
    const waterUsage = multipleDeviceUsage?.usage?.[0]

    if (chartImageData && currentDate && waterUsage?.length) {
      const pdf = buildUsageNavigatorPdf({
        chartImageData,
        deviceName: deviceSelected.deviceName,
        userUom,
        waterUsage,
      })

      const fileName = `${deviceSelected.deviceName}-${format(currentDate, 'dd-MM-yyyy')}`
      pdf.save(fileName)
    }
  }

  const getHeaders = () => {
    const periodColumns: Array<any> = [
      { name: 'Date', key: 'date', width: '120px' },
      { name: `Usage in ${userUom}`, key: 'usage', width: '220px' },
    ]
    return [...periodColumns, ...COLUMNS].map((header: any) => (
      <Table.Th key={header.key} style={{ width: header.width ?? '80px', fontSize: '.75rem' }}>
        {header.name}
      </Table.Th>
    ))
  }

  const getRows = (waterUsage: any) => {
    const groupedByDay = waterUsage.reduce((acc: any, dailyUsage: any) => {
      const date = dailyUsage.date.split('T')[0]
      const flow = dailyUsage.flow
      if (!acc[date]) {
        acc[date] = {
          totalFlow: flow,
          date,
          ['totalFlowHour' + dailyUsage.hour]: flow,
        }
      } else {
        acc[date].totalFlow += flow
        acc[date]['totalFlowHour' + dailyUsage.hour] = flow
      }
      return acc
    }, {})

    return Object.values(groupedByDay).map((usage: any, idx: number) => {
      const renderHourCell = (hour: number) => {
        const value = usage[`totalFlowHour${hour}`]
        return value !== null ? (value !== 0 ? value : 0) : '-'
      }
      return (
        <Table.Tr key={idx} style={{ fontSize: '.8rem' }}>
          <Table.Td>{usage.date}</Table.Td>
          <Table.Td>{roundToTwo(usage.totalFlow)}</Table.Td>
          {[...Array(24).keys()].map((hour) => (
            <Table.Td key={hour}>{renderHourCell(hour)}</Table.Td>
          ))}
        </Table.Tr>
      )
    })
  }

  const isPrevDisabled =
    isFetching ||
    (currentDate !== null &&
      startOfDay(currentDate).getTime() <=
      startOfDay(new Date(deviceSelected.deviceSettings.usageStartDate)).getTime())

  const totalUsageSummary = (
    <Text ta={isSmallScreen ? 'left' : 'center'}>
      <Text span fw={700}>Total Usage: </Text>
      {totalUsage} {userUom}
    </Text>
  )

  return (
    <Modal.Root
      opened={show}
      onClose={onHide}
      size={isFullscreen ? '100%' : 'xl'}
      fullScreen={isFullscreen}
      centered
      closeOnClickOutside={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Text fw={600}>
              {deviceSelected.deviceName} - {deviceSelected.deviceId}
            </Text>
          </Modal.Title>
          <Group>
            <ActionIcon
              variant={'subtle'}
              c={'dimmed'}
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </ActionIcon>
            <Modal.CloseButton />
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Stack gap='sm'>
            <Stack gap='xs'>
              <Text size='sm'>{currentDate && format(currentDate, 'EEEE, MMMM do yyyy')}</Text>
              <Group
                justify='space-between'
                align='stretch'
                wrap={isSmallScreen ? 'wrap' : 'nowrap'}
              >
                <Button
                  variant='default'
                  onClick={onPreviousButtonClick}
                  disabled={isPrevDisabled}
                  style={{ flex: isSmallScreen ? '1 1 100%' : undefined }}
                >
                  Previous Day
                </Button>
                {!currentDate || !isSameDay(currentDate, getToday()) ? (
                  <Button
                    onClick={onNextButtonClick}
                    disabled={isFetching}
                    style={{ flex: isSmallScreen ? '1 1 100%' : undefined }}
                  >
                    Next Day
                  </Button>
                ) : hasPermissions(permissions, ['READ:DEVICE:DLF']) &&
                deviceSelected.deviceVendor === 'SMARTFLOW' ? (
                  <Button
                    onClick={onRefreshButtonClick}
                    disabled={isFetching || isRefreshing}
                    style={{ flex: isSmallScreen ? '1 1 100%' : undefined }}
                  >
                    Refresh
                  </Button>
                ) : null}
              </Group>
            </Stack>

            {isFetching || isLoading || isRefreshing ? (
              <Center my='xl'>
                <Loader />
              </Center>
            ) : (
              multipleDeviceUsage && (
                <Stack gap='sm'>
                  <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tabs.List>
                      <Tabs.Tab value='usage' leftSection={<BsBarChartLine />} />
                      <Tabs.Tab value='table' leftSection={<BsTable />} />
                    </Tabs.List>

                    <Tabs.Panel value='usage'>
                      <Center>
                        <Stack
                          gap='sm'
                          style={{
                            width: '100%',
                            maxWidth: isSmallScreen ? '100%' : chartContentMaxWidth,
                          }}
                        >
                          <div
                            ref={chartContainerRef}
                            style={{
                              position: 'relative',
                              width: '100%',
                              minHeight: chartHeight,
                              height: chartHeight,
                              maxHeight: isFullscreen ? '720px' : undefined,
                            }}
                          >
                            <Bar
                              id='daily-usage-chart'
                              data={buildData(
                                deviceSelected,
                                multipleDeviceUsage.usage[0],
                                isSmallScreen,
                                maxBarThickness,
                                barPercentage,
                                categoryPercentage,
                              )}
                              options={options}
                              ref={chartRef}
                            />
                          </div>
                          {totalUsageSummary}
                        </Stack>
                      </Center>
                    </Tabs.Panel>

                    <Tabs.Panel value='table'>
                      <Stack gap='sm'>
                        {totalUsageSummary}
                        <ScrollArea>
                          <Table id='usage-table'>
                            <Table.Thead>
                              <Table.Tr>{getHeaders()}</Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>{getRows(multipleDeviceUsage?.usage[0])}</Table.Tbody>
                          </Table>
                        </ScrollArea>
                      </Stack>
                    </Tabs.Panel>
                  </Tabs>
                </Stack>
              )
            )}

            <Group justify='space-between' mt='sm'>
              <Button
                variant='default'
                onClick={downloadWaterGraph}
                leftSection={<span className='fas fa-download' />}
              >
                Export PDF
              </Button>
              <Button onClick={onHide}>Close</Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}

export default UsageNavigator
